import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { cn } from '#app/utils/misc.tsx'

export interface TextareaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
	({ className, ...props }, ref) => {
		return (
			<textarea
				className={cn(
					'flex min-h-[80px] w-full border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid',
					className,
				)}
				ref={ref}
				{...props}
			/>
		)
	},
)
Textarea.displayName = 'Textarea'

const PublicStyledTextarea = React.forwardRef<
	HTMLTextAreaElement,
	TextareaProps & {
		showLines?: boolean
	}
>(({ className, showLines = true, ...props }, ref) => {
	const internalRef = useRef<HTMLTextAreaElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const [lines, setLines] = useState<number[]>([])

	// Use the passed ref or internal ref
	const textareaRef =
		(ref as React.RefObject<HTMLTextAreaElement>) || internalRef

	const calculateLines = useCallback(() => {
		if (!textareaRef.current || !containerRef.current || !showLines) return

		const textarea = textareaRef.current

		// Get computed styles
		const computedStyle = window.getComputedStyle(textarea)

		// Create temporary element to get actual computed clamp values
		const tempDiv = document.createElement('div')
		tempDiv.style.fontSize = computedStyle.fontSize
		tempDiv.style.lineHeight = computedStyle.lineHeight
		tempDiv.style.fontFamily = computedStyle.fontFamily
		tempDiv.style.visibility = 'hidden'
		tempDiv.style.position = 'absolute'
		tempDiv.style.top = '-9999px'
		document.body.appendChild(tempDiv)

		const lineHeightPx = parseFloat(getComputedStyle(tempDiv).lineHeight)
		document.body.removeChild(tempDiv)

		// Get textarea dimensions
		const textareaHeight = Math.max(
			textarea.scrollHeight,
			textarea.clientHeight,
		)
		const paddingTop = parseFloat(computedStyle.paddingTop)
		const paddingBottom = parseFloat(computedStyle.paddingBottom)

		// Calculate available height for text
		const availableHeight = textareaHeight - paddingTop - paddingBottom

		// Calculate number of lines needed (add buffer for dynamic content)
		const numberOfLines = Math.ceil(availableHeight / lineHeightPx) + 3

		// Generate line positions
		const linePositions: number[] = []
		for (let i = 0; i < numberOfLines; i++) {
			// Position lines at the baseline of each text line
			// Account for font-serif baseline and padding
			const baselineOffset = lineHeightPx * 0.9 // Adjusted for serif fonts
			const linePosition = paddingTop + i * lineHeightPx + baselineOffset
			linePositions.push(linePosition)
		}

		setLines(linePositions)
	}, [showLines, textareaRef])

	// Auto-resize functionality
	const handleInput = useCallback(
		(e: React.FormEvent<HTMLTextAreaElement>) => {
			const target = e.target as HTMLTextAreaElement
			target.style.height = 'auto'
			target.style.height = `${target.scrollHeight}px`
			calculateLines()
		},
		[calculateLines],
	)

	useEffect(() => {
		calculateLines()
	}, [props.value, calculateLines])

	useEffect(() => {
		if (!textareaRef.current || !showLines) return

		const resizeObserver = new ResizeObserver(() => {
			// Small delay to ensure DOM has updated
			setTimeout(calculateLines, 0)
		})

		resizeObserver.observe(textareaRef.current)

		return () => {
			resizeObserver.disconnect()
		}
	}, [calculateLines, showLines, textareaRef])

	// Initial calculation on mount
	useEffect(() => {
		if (showLines) {
			// Delay initial calculation to ensure styles are applied
			setTimeout(calculateLines, 100)
		}
	}, [showLines, calculateLines])

	return (
		<div ref={containerRef} className="relative">
			{/* Background lines - only render if showLines is true */}
			{showLines && (
				<div className="pointer-events-none absolute inset-0 overflow-hidden">
					{lines.map((linePosition, index) => (
						<div
							key={`line-${index}`}
							className="absolute left-0 right-0 h-px bg-gray-300"
							style={{
								top: `${linePosition}px`,
								marginLeft: '4px', // Match px-1 (4px)
								marginRight: '4px',
							}}
						/>
					))}
				</div>
			)}
			<textarea
				className={cn(
					'flex min-h-[80px] w-full resize-none border-none bg-transparent px-0 py-1 font-serif text-[clamp(25px,calc(2vw+1rem),3rem)] font-light leading-[clamp(25px,calc(2vw+1rem),3rem)] ring-offset-background placeholder:text-muted-foreground focus:border-none disabled:cursor-not-allowed disabled:opacity-50',
					className,
				)}
				ref={textareaRef}
				onInput={handleInput}
				{...props}
			/>
		</div>
	)
})
Textarea.displayName = 'Textarea'

export { Textarea, PublicStyledTextarea }
